import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import Layout from "../components/Layout";
import { api } from "../lib/api";

// Custom divIcons
const recommendedIcon = L.divIcon({ className: "", html: '<div class="sp-recommended-marker"></div>', iconSize: [14, 14], iconAnchor: [7, 7] });
const visitedIcon = L.divIcon({ className: "", html: '<div class="sp-visited-marker"></div>', iconSize: [18, 18], iconAnchor: [9, 9] });
const topIcon = L.divIcon({ className: "", html: '<div class="sp-top-marker"></div>', iconSize: [22, 22], iconAnchor: [11, 11] });

const TOGGLES = [
    { key: "Sun",     label: "Sun" },
    { key: "Moon",    label: "Moon" },
    { key: "Venus",   label: "Venus" },
    { key: "Jupiter", label: "Jupiter" },
];

export default function MyWorld() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [visible, setVisible] = useState({ Sun: true, Moon: true, Venus: true, Jupiter: true });
    const [year, setYear] = useState("all");

    useEffect(() => {
        api.get("/world")
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || e.message));
    }, []);

    const toggle = (k) => setVisible((v) => ({ ...v, [k]: !v[k] }));
    const allOn = Object.values(visible).every(Boolean);
    const toggleAll = () => {
        const next = !allOn;
        setVisible({ Sun: next, Moon: next, Venus: next, Jupiter: next });
    };

    if (error) return <Layout><div className="sp-container py-24" data-testid="world-error">{error}</div></Layout>;
    if (!data) return <Layout><div className="sp-container py-24 sp-overline" data-testid="world-loading">Drawing your map…</div></Layout>;

    const filteredTrips = data.trips.filter((t) => {
        if (year === "all") return true;
        return true; // future: real year filter
    });

    const topId = data.top_destination?.id;

    return (
        <Layout>
            <div className="sp-container pt-12 pb-6">
                <div className="sp-overline">My World</div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif mt-3 leading-[1.05]">A map of where the stars take you.</h1>

                <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="world-toggles">
                    <button onClick={toggleAll} className="sp-pill hover:bg-ink hover:text-sand-50 transition-colors" data-testid="world-toggle-all">
                        {allOn ? "Hide all lines" : "Show all lines"}
                    </button>
                    {TOGGLES.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => toggle(t.key)}
                            className="sp-pill transition-colors"
                            style={{ color: visible[t.key] ? "#2C2A29" : "#8A8581", borderColor: visible[t.key] ? "rgba(44,42,41,0.3)" : "rgba(44,42,41,0.1)" }}
                            data-testid={`world-toggle-${t.key.toLowerCase()}`}
                        >
                            <span className="w-2 h-2 rounded-full" style={{ background: data.lines.find((l) => l.planet === t.key)?.color || "#999" }} />
                            {t.label}
                        </button>
                    ))}
                    <div className="ml-auto flex items-center gap-3">
                        <span className="sp-overline">Filter</span>
                        <select
                            value={year}
                            onChange={(e) => setYear(e.target.value)}
                            className="bg-transparent border border-ink/15 rounded-full px-4 py-1.5 text-sm"
                            data-testid="world-year-filter"
                        >
                            <option value="all">All time</option>
                            <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
                            <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="sp-container pb-24">
                <div className="rounded-2xl overflow-hidden border border-ink/10" style={{ height: "70vh" }} data-testid="world-map">
                    <MapContainer center={[20, 10]} zoom={2} scrollWheelZoom worldCopyJump style={{ height: "100%", width: "100%" }}>
                        <TileLayer
                            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                            attribution='&copy; <a href="https://carto.com/">carto.com</a>'
                        />
                        {data.lines.filter((l) => visible[l.planet]).map((l) => (
                            <Polyline
                                key={l.planet}
                                positions={l.points}
                                pathOptions={{ color: l.color, weight: 1.5, dashArray: "4 6", opacity: 0.7 }}
                            />
                        ))}
                        {data.recommended.map((d) => (
                            <Marker
                                key={`rec-${d.id}`}
                                position={[d.lat, d.lng]}
                                icon={d.id === topId ? topIcon : recommendedIcon}
                                data-testid={`world-marker-rec-${d.id}`}
                            >
                                <Popup>
                                    <div className="font-serif text-lg">{d.city}</div>
                                    <div className="text-xs text-ink-muted">{d.country}</div>
                                    <div className="text-xs mt-2">Score {d.score} · {d.planet}</div>
                                </Popup>
                            </Marker>
                        ))}
                        {filteredTrips.filter((t) => t.visited).map((t) => (
                            <Marker
                                key={`visited-${t.trip_id}`}
                                position={[t.lat, t.lng]}
                                icon={visitedIcon}
                            >
                                <Popup>
                                    <div className="font-serif text-lg">{t.city}</div>
                                    <div className="text-xs text-ink-muted">{t.country} · Visited</div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>

                <div className="mt-8 grid sm:grid-cols-3 gap-4 text-sm" data-testid="world-legend">
                    <div className="flex items-center gap-3"><div className="sp-recommended-marker" /><span>Recommended destination</span></div>
                    <div className="flex items-center gap-3"><div className="sp-visited-marker" style={{ width: 14, height: 14 }} /><span>Visited (saved &amp; marked complete)</span></div>
                    <div className="flex items-center gap-3"><div className="sp-top-marker" style={{ width: 14, height: 14 }} /><span>Today&apos;s high-scoring location</span></div>
                </div>
            </div>
        </Layout>
    );
}
